import { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2, Heading3,
  Link as LinkIcon, Image as ImageIcon, Library, Quote, Undo, Redo, AlignLeft, AlignCenter, AlignRight, Video,
} from "lucide-react";
import MediaLibraryModal from "./MediaLibraryModal";

const FigureImage = Image.extend({
  name: "figureImage",
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-size": { default: "full", parseHTML: (el) => el.getAttribute("data-size") || "full", renderHTML: (a) => ({ "data-size": a["data-size"] }) },
      "data-align": { default: "center", parseHTML: (el) => el.getAttribute("data-align") || "center", renderHTML: (a) => ({ "data-align": a["data-align"] }) },
      "data-caption": { default: "", parseHTML: (el) => el.getAttribute("data-caption") || "", renderHTML: (a) => ({ "data-caption": a["data-caption"] }) },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const size = HTMLAttributes["data-size"] || "full";
    const align = HTMLAttributes["data-align"] || "center";
    const caption = HTMLAttributes["data-caption"] || "";
    const figClass = `editor-figure size-${size} align-${align}`;
    const inner = ["img", { ...HTMLAttributes, class: undefined }];
    if (caption) {
      return ["figure", { class: figClass }, inner, ["figcaption", caption]];
    }
    return ["figure", { class: figClass }, inner];
  },
});

const VideoNode = Node.create({
  name: "videoNode",
  group: "block",
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
      mime: { default: "video/mp4" },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-video]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes({ "data-video": true, class: "video-embed", style: "margin:1.5rem 0;" }),
      ["video", { controls: true, src: HTMLAttributes.src, style: "width:100%;max-height:480px;", type: HTMLAttributes.mime }],
    ];
  },
});

const IframeNode = Node.create({
  name: "iframeNode",
  group: "block",
  atom: true,
  addAttributes() {
    return { src: { default: null } };
  },
  parseHTML() {
    return [{ tag: "div[data-iframe]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes({ "data-iframe": true, class: "video-embed", style: "margin:1.5rem 0;" }),
      ["iframe", { src: HTMLAttributes.src, frameborder: "0", allowfullscreen: true, style: "width:100%;aspect-ratio:16/9;" }],
    ];
  },
});

const TipTapEditor = ({ value, onChange, dir = "auto", placeholder = "ابدأ الكتابة…" }) => {
  const [showMedia, setShowMedia] = useState(false);
  const [mediaTarget, setMediaTarget] = useState("body");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", class: "text-gold underline" } }),
      FigureImage.configure({ inline: false, allowBase64: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      VideoNode,
      IframeNode,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tiptap-content min-h-[280px] focus:outline-none",
        dir,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== current) {
      editor.commands.setContent(value || "", false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("الرابط:", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertMediaItem = (item) => {
    if (item.kind === "image") {
      const size = window.prompt("حجم الصورة: full | medium | small", "full") || "full";
      const align = window.prompt("المحاذاة: left | center | right", "center") || "center";
      const caption = window.prompt("نص أسفل الصورة (اختياري):", "") || "";
      editor.chain().focus().insertContent({
        type: "figureImage",
        attrs: {
          src: item.url,
          alt: item.name || "",
          "data-size": ["full", "medium", "small"].includes(size) ? size : "full",
          "data-align": ["left", "center", "right"].includes(align) ? align : "center",
          "data-caption": caption,
        },
      }).run();
    } else if (item.kind === "video") {
      editor.chain().focus().insertContent({
        type: "videoNode",
        attrs: { src: item.url, mime: item.mime || "video/mp4" },
      }).run();
    } else {
      editor.chain().focus().insertContent(`<a href="${item.url}" target="_blank" rel="noopener">${item.name || item.url}</a>`).run();
    }
    setShowMedia(false);
  };

  const insertImageByUrl = () => {
    const src = window.prompt("رابط الصورة:");
    if (!src) return;
    const size = window.prompt("حجم: full | medium | small", "full") || "full";
    const align = window.prompt("محاذاة: left | center | right", "center") || "center";
    const caption = window.prompt("نص أسفل الصورة:", "") || "";
    editor.chain().focus().insertContent({
      type: "figureImage",
      attrs: {
        src,
        alt: "",
        "data-size": ["full", "medium", "small"].includes(size) ? size : "full",
        "data-align": ["left", "center", "right"].includes(align) ? align : "center",
        "data-caption": caption,
      },
    }).run();
  };

  const insertVideo = () => {
    const url = window.prompt("رابط الفيديو (YouTube أو رابط مباشر):");
    if (!url) return;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (yt) {
      editor.chain().focus().insertContent({
        type: "iframeNode",
        attrs: { src: `https://www.youtube.com/embed/${yt[1]}` },
      }).run();
    } else if (vimeo) {
      editor.chain().focus().insertContent({
        type: "iframeNode",
        attrs: { src: `https://player.vimeo.com/video/${vimeo[1]}` },
      }).run();
    } else {
      editor.chain().focus().insertContent({
        type: "videoNode",
        attrs: { src: url, mime: "video/mp4" },
      }).run();
    }
  };

  const Btn = ({ on, active, title, children, testId }) => (
    <button
      type="button"
      onClick={on}
      title={title}
      data-testid={testId}
      className={`px-2.5 py-2 text-sm transition-colors ${active ? "text-gold bg-gold/10" : "text-white/65 hover:text-bone hover:bg-white/5"}`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-white/15 bg-ink-soft">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-white/10 bg-ink/70 px-2 py-1" dir="ltr">
        <Btn on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="عريض" testId="ed-bold"><Bold size={14} /></Btn>
        <Btn on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="مائل" testId="ed-italic"><Italic size={14} /></Btn>
        <Btn on={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="تحته خط" testId="ed-underline"><UnderlineIcon size={14} /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="عنوان كبير" testId="ed-h1"><Heading1 size={14} /></Btn>
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="عنوان متوسط" testId="ed-h2"><Heading2 size={14} /></Btn>
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="عنوان صغير" testId="ed-h3"><Heading3 size={14} /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="قائمة نقطية" testId="ed-ul"><List size={14} /></Btn>
        <Btn on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="قائمة مرقمة" testId="ed-ol"><ListOrdered size={14} /></Btn>
        <Btn on={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="اقتباس"><Quote size={14} /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn on={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="محاذاة يمين"><AlignRight size={14} /></Btn>
        <Btn on={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="محاذاة وسط"><AlignCenter size={14} /></Btn>
        <Btn on={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="محاذاة يسار"><AlignLeft size={14} /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn on={setLink} active={editor.isActive("link")} title="إضافة رابط" testId="ed-link"><LinkIcon size={14} /></Btn>
        <Btn on={insertImageByUrl} title="إضافة صورة من رابط" testId="ed-image-url"><ImageIcon size={14} /></Btn>
        <Btn on={insertVideo} title="إدراج فيديو" testId="ed-video"><Video size={14} /></Btn>
        <Btn on={() => { setMediaTarget("body"); setShowMedia(true); }} title="المكتبة" testId="ed-media-library"><Library size={14} /></Btn>
        <span className="flex-1" />
        <Btn on={() => editor.chain().focus().undo().run()} title="تراجع"><Undo size={14} /></Btn>
        <Btn on={() => editor.chain().focus().redo().run()} title="تقدّم"><Redo size={14} /></Btn>
      </div>

      <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      <MediaLibraryModal
        open={showMedia}
        onClose={() => setShowMedia(false)}
        onSelect={insertMediaItem}
        kind="any"
      />
      <input type="hidden" value={mediaTarget} readOnly />
    </div>
  );
};

export default TipTapEditor;
